import { Button, Flex } from "@chakra-ui/react";
import "@uppy/core/dist/style.css";
import "@uppy/drag-drop/dist/style.css";
import "@uppy/progress-bar/dist/style.css";
import { t } from "i18next";
import { useCallback, useEffect, useState } from "react";
import {
  ContractDto,
  SignedDocumentDto,
  TextDocumentDto,
  useCreateContractTextDocumentMutation,
  useListTextDocumentsForContractQuery,
  useUpdateContractMutation,
  ViewDocumentAsOwnerDto,
} from "../../../../autogen/bff-api";
import { CombinedDocuments } from "../../../../common/documents/CombinedDocuments";
import { IncompleteFileUpload, isIncompleteFileUpload } from "../../../../common/documents/UppyUploader/FileRow";
import { useUppyUploader } from "../../../../common/documents/UppyUploader/useUppyUploader";
import { useAppDispatch } from "../../../../common/redux/hooks";
import { loadContractThunk } from "../../../../common/redux/thunks/contract/load-contract-thunk";
import { getCounterpartyContext, requireDocumentFields, requireEditingMetaFields } from "../../typeguards";
import { useContractState } from "../useContractState";
import "./Docs.scss";
interface Props {
  previousStep: () => void;
  nextStep: () => void;
}

export const Docs = ({ previousStep, nextStep }: Props) => {
  const dispatch = useAppDispatch();
  const contractState = useContractState();
  const documentFields = requireDocumentFields(contractState.contract as ContractDto);
  const editingMetaFields = requireEditingMetaFields(contractState.contract as ContractDto);
  const [createTextDocument, { isLoading: isCreatingTextDocument }] = useCreateContractTextDocumentMutation();
  const {
    data: textDocumentRes,
    isLoading: isLoadingTextDocuments,
    refetch: refetchTextDocuments,
  } = useListTextDocumentsForContractQuery({
    contractId: contractState.id,
  });
  const [updateContract] = useUpdateContractMutation();

  const createTextDocumentForContract = useCallback(() => {
    return createTextDocument({
      createContractTextDocumentRequest: {
        contractId: contractState.id,
        name: "",
        text: "",
        tags: [],
      },
    }).unwrap();
  }, [contractState.id, createTextDocument]);

  const loadContract = useCallback(() => {
    dispatch(loadContractThunk({ contractId: contractState.id }));
  }, [contractState.id, dispatch]);

  const refetchDocuments = useCallback(() => {
    loadContract();
    void refetchTextDocuments();
  }, [loadContract, refetchTextDocuments]);

  const removeUploadedDocument = useCallback(
    async (documentId: string) => {
      await updateContract({
        contractId: contractState.id,
        updateContractRequest: {
          removeDocument: documentId,
        },
      }).unwrap();
    },
    [contractState.id, updateContract]
  );

  const { uppy, allFiles, documents, setDocuments, setUploads, removeUpload } = useUppyUploader({
    entityId: contractState.id,
    entityType: "Contract",
    reloadEntity: loadContract,
  });

  const [signedDocuments, setSignedDocuments] = useState<SignedDocumentDto[]>([]);
  const [textDocuments, setTextDocuments] = useState<TextDocumentDto[]>([]);

  useEffect(() => {
    setSignedDocuments(documentFields.signedDocuments);
  }, [documentFields.signedDocuments]);

  useEffect(() => {
    setUploads(editingMetaFields.uploads);
  }, [editingMetaFields.uploads, setUploads]);

  useEffect(() => {
    setDocuments(documentFields.documents);
  }, [documentFields.documents, setDocuments]);

  useEffect(() => {
    if (textDocumentRes) setTextDocuments(textDocumentRes.documents);
  }, [textDocumentRes]);

  const counterpartyContext = getCounterpartyContext(contractState.contract);

  return (
    <>
      <Flex flexDirection="column" pt="8">
        <CombinedDocuments
          canEditDocumentNames={true}
          canEditTextDocuments={true}
          canDeleteDocuments={true}
          contractId={contractState.id}
          documentSignatureConfig={
            counterpartyContext
              ? {
                  contractId: contractState.id,
                  counterpartyContext,
                }
              : undefined
          }
          canSendForSigning={contractState.contract.canEditEverything}
          signedDocuments={signedDocuments}
          setSignedDocuments={setSignedDocuments}
          refetchDocuments={refetchDocuments}
          uploadedDocuments={documents}
          textDocuments={textDocuments}
          createTextDocument={createTextDocumentForContract}
          isCreatingTextDocument={isCreatingTextDocument}
          setUploadedDocuments={setDocuments}
          setTextDocuments={setTextDocuments}
          removeUploadedDocument={(document: ViewDocumentAsOwnerDto) => removeUploadedDocument(document.id)}
          isLoading={isLoadingTextDocuments}
          documentUploadConfig={{
            dropTargetId: "editContractDocumentsTarget",
            uppy,
            dropTargetHelperText: `${t("Upload your documents")}`,
            uploads: allFiles.filter((f) => isIncompleteFileUpload(f)) as IncompleteFileUpload[],
            removeUpload,
          }}
          isTagsColumnVisible={true}
          canEditTags={true}
        />
      </Flex>
      <Flex pt="8" pb="10">
        <Button variant={"outline"} mr="5px" w="100%" colorScheme={"teal"} onClick={previousStep}>
          {t("Previous")}
        </Button>
        <Button variant={"solid"} w="100%" colorScheme={"teal"} onClick={nextStep}>
          {t("Next")}
        </Button>
      </Flex>
    </>
  );
};
